import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Typography, Container, Box, Button, Grid, Card, 
  CardContent, CardActions, Dialog, DialogTitle, 
  DialogContent, DialogActions, TextField, MenuItem, CircularProgress, 
  Drawer, IconButton, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Stack
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

function Dashboard() {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [open, setOpen] = useState(false);
  const [manageDrawerOpen, setManageDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false); 
  const [fetching, setFetching] = useState(true);
  const [accountLoading, setAccountLoading] = useState(false);
  
  const [reports, setReports] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);

  const [shopData, setShopData] = useState({ name: '', address: '', category_id: '' });

  const [accountData, setAccountData] = useState({
    shop_id: '',
    date: new Date().toISOString().split('T')[0],
    sell: 0, market: 0, visacard: 0, snacks: 0, drivar_bill: 0, others: 0
  });

  const categories = [
    { id: 1, label: 'Electronics' },
    { id: 2, label: 'Footwear' },
    { id: 3, label: 'Grocery' }
  ];

  useEffect(() => { fetchShops(); }, []);

  const fetchShops = async () => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/'); return; }
    try {
      const response = await axios.get('https://posapi.mahfuzshahin.com/api/shops', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.status) setShops(response.data.data);
    } catch (error) {
      if (error.response?.status === 401) navigate('/');
    } finally { setFetching(false); }
  };

  const handleManageClick = (shop) => {
    setSelectedShop(shop);
    setAccountData({ ...accountData, shop_id: shop.id });
    setManageDrawerOpen(true);
    fetchReports(shop.id);
  };

  const fetchReports = async (shopId) => {
    setAccountLoading(true);
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`https://posapi.mahfuzshahin.com/api/account/${shopId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.status) {
        setReports(response.data.data);
      }
    } catch (error) {
      console.error("Report Fetch Error", error);
      setReports([]);
    } finally {
      setAccountLoading(false);
    }
  };

  const handleAccountChange = (e) => {
    const { name, value } = e.target;
    setAccountData({ ...accountData, [name]: name === 'date' ? value : Number(value) });
  };

  const handleAccountSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setAccountLoading(true);
    try {
      await axios.post('https://posapi.mahfuzshahin.com/api/account', accountData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert("Entry saved successfully!");
      fetchReports(selectedShop.id); 
    } catch (error) {
      alert("Failed to submit data");
    } finally { setAccountLoading(false); }
  };

  const handleCreateShop = async () => {
    const token = localStorage.getItem('token');
    const storedUser = JSON.parse(localStorage.getItem('user'));
    setLoading(true);
    try {
      await axios.post('https://posapi.mahfuzshahin.com/api/shops', { ...shopData, user_id: storedUser?.id || 3 }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setOpen(false);
      setShopData({ name: '', address: '', category_id: '' });
      fetchShops();
    } catch (error) {
      alert("Error creating shop");
    } finally { setLoading(false); }
  };

  const handleDeleteShop = async (id) => {
      if(!window.confirm("Confirm shop deletion?")) return;
      const token = localStorage.getItem('token');
      try {
          await axios.delete(`https://posapi.mahfuzshahin.com/api/shops/${id}`, {
              headers: { Authorization: `Bearer ${token}` }
          });
          setManageDrawerOpen(false);
          fetchShops();
      } catch (e) { alert("Delete failed"); }
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
      <AppBar position="sticky" elevation={0} sx={{ backgroundColor: '#fff', color: '#2c3e50', borderBottom: '1px solid #e0e6ed' }}>
        <Toolbar>
          <StorefrontIcon sx={{ mr: 1, color: '#2196f3' }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 900, letterSpacing: 0.5 }}>SHOPMANAGER</Typography>
          <Button color="inherit" sx={{ fontWeight: 600 }} onClick={() => { localStorage.clear(); navigate('/'); }}>Logout</Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" fontWeight="900" sx={{ color: '#1a2027' }}>My Properties</Typography>
          <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => setOpen(true)} sx={{ borderRadius: 2, px: 3, bgcolor: '#2196f3' }}>
            New Shop
          </Button>
        </Box>

        {fetching ? <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 10 }} /> : (
          <Grid container spacing={3}>
            {shops.map((shop) => (
              <Grid item xs={12} sm={6} md={4} key={shop.id}>
                <Card sx={{ borderRadius: 4, transition: '0.2s', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' } }}>
                  <CardContent sx={{ pb: 1 }}>
                    <Typography variant="h6" fontWeight="800" gutterBottom>{shop.name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>{shop.address}</Typography>
                    <Box sx={{ mt: 2, display: 'inline-block', px: 1.5, py: 0.5, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 700 }}>
                            {shop.category?.name || 'Uncategorized'}
                        </Typography>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 2 }}>
                    <Button variant="contained" fullWidth startIcon={<SettingsIcon />} onClick={() => handleManageClick(shop)} sx={{ borderRadius: 2, bgcolor: '#37474f', '&:hover': { bgcolor: '#263238' } }}>
                      Manage
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      <Drawer anchor="right" open={manageDrawerOpen} onClose={() => setManageDrawerOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: '650px' }, borderLeft: 'none' } }}>
        {selectedShop && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 3, bgcolor: '#fff', borderBottom: '1px solid #f0f0f0' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h5" fontWeight="900">{selectedShop.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{selectedShop.address}</Typography>
                </Box>
                <IconButton onClick={() => setManageDrawerOpen(false)} sx={{ bgcolor: '#f5f5f5' }}><CloseIcon /></IconButton>
              </Stack>
              <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => handleDeleteShop(selectedShop.id)} sx={{ mt: 1, textTransform: 'none' }}>Remove Shop</Button>
            </Box>

            <Box sx={{ p: 3, overflowY: 'auto', flexGrow: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 800, color: '#1976d2' }}>DAILY DATA ENTRY</Typography>
              <form onSubmit={handleAccountSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}><TextField label="Date" type="date" name="date" size="small" fullWidth value={accountData.date} onChange={handleAccountChange} InputLabelProps={{ shrink: true }} /></Grid>
                  {['sell', 'market', 'visacard', 'snacks', 'drivar_bill', 'others'].map(field => (
                    <Grid item xs={4} key={field}><TextField label={field.replace('_', ' ').toUpperCase()} name={field} type="number" size="small" fullWidth onChange={handleAccountChange} /></Grid>
                  ))}
                  <Grid item xs={12}><Button type="submit" variant="contained" fullWidth disableElevation sx={{ borderRadius: 2, py: 1, fontWeight: 700 }}>Save Record</Button></Grid>
                </Grid>
              </form>

              <Divider sx={{ my: 4 }} />
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 800, color: '#1976d2' }}>FINANCIAL HISTORY</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem' }}>DATE</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.75rem' }}>SELL</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.75rem' }}>MARKET</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.75rem' }}>VISA</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.75rem' }}>SNACK</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.75rem' }}>DRIVER</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.75rem' }}>OTHER</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {accountLoading ? (
                      <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}><CircularProgress size={20} /></TableCell></TableRow>
                    ) : reports.length === 0 ? (
                      <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}>No records found</TableCell></TableRow>
                    ) : (
                      reports.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{row.date}</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main', fontWeight: 800, fontSize: '0.75rem' }}>{row.sell}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{row.market}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#1976d2' }}>{row.visacard}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{row.snacks}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{row.drivar_bill}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{row.others}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        )}
      </Drawer>

      <Dialog open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Register New Shop</DialogTitle>
        <DialogContent sx={{ minWidth: 380 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Shop Name" fullWidth variant="outlined" onChange={(e) => setShopData({...shopData, name: e.target.value})} />
            <TextField label="Address" fullWidth variant="outlined" multiline rows={2} onChange={(e) => setShopData({...shopData, address: e.target.value})} />
            <TextField select label="Category" fullWidth variant="outlined" value={shopData.category_id} onChange={(e) => setShopData({...shopData, category_id: e.target.value})}>
              {categories.map((opt) => <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpen(false)} sx={{ fontWeight: 700, color: 'text.secondary' }}>Cancel</Button>
          <Button onClick={handleCreateShop} variant="contained" disableElevation sx={{ borderRadius: 2, px: 4, fontWeight: 700 }}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Dashboard;